/**
 * DemoCourierFlow - Shows manager-to-courier handoff workflow
 *
 * Left: Process trigger (selected orders + generate button) → Success modal
 * Right: Phone frame with realistic courier order cards (matching real app)
 *
 * GUARDRAIL: This component uses only static demo data.
 * No imports from hooks/, features/, or services/.
 */

import { CheckCircle2, ArrowRight, MapPin, Phone, Copy, Link2, Check, X } from "lucide-react";
import { getDemoCourierOrders, STATUS_COLORS } from "@/lib/demoData";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useMemo } from "react";

function ModalSnapshot() {
  const { t } = useLanguage();
  
  return (
    <div className="w-full max-w-[300px]">
      {/* Process trigger indicator */}
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span className="text-[11px] text-muted-foreground">3 {t("landing.demo.ordersSelected")}</span>
      </div>

      {/* The trigger button (visual mockup) */}
      <div className="bg-primary/10 border border-primary/30 rounded-md px-3 py-2 mb-3 flex items-center gap-2 cursor-default hover:bg-primary/15 transition-colors">
        <Link2 className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-medium text-primary">{t("landing.demo.generateLink")}</span>
        <ArrowRight className="h-3 w-3 text-primary/60 ml-auto" />
      </div>

      {/* Success modal result */}
      <div className="bg-background border border-border rounded-lg p-4 shadow-sm">
        {/* Success header */}
        <div className="flex items-center gap-2 p-2.5 bg-emerald-500/10 rounded-md mb-4">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">{t("landing.demo.linkReady")}</span>
        </div>

        {/* Link display */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 bg-muted p-2.5 rounded-md">
            <span className="font-mono text-xs text-foreground flex-1 truncate">orderflow.ge/d/abc123</span>
            <Copy className="h-3.5 w-3.5 text-muted-foreground" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-muted/50 p-2.5 rounded-md">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t("landing.demo.orders")}</span>
              <p className="text-lg font-bold text-foreground mt-0.5">3</p>
            </div>
            <div className="bg-muted/50 p-2.5 rounded-md">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t("landing.demo.expires")}</span>
              <p className="text-sm font-medium text-foreground mt-1">{t("landing.demo.endOfDay")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoCourierCard({ order, t }: { order: ReturnType<typeof getDemoCourierOrders>[0]; t: (key: string) => string }) {
  const statusLabel = t(`landing.demo.statuses.${order.status}`) || order.status;
  const statusColor = STATUS_COLORS[order.status] || "bg-muted text-muted-foreground";
  const isDelivered = order.status === "Delivered";
  const isReady = order.status === "Ready";
  const isOutForDelivery = order.status === "Out for Delivery";

  return (
    <div
      className={cn(
        "bg-background border rounded-lg p-3 shadow-sm",
        isDelivered ? "border-emerald-200/50 dark:border-emerald-800/30 opacity-70" : "border-border",
      )}
    >
      {/* Header: Customer + Order Number */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              "text-[11px] font-semibold truncate",
              isDelivered ? "text-muted-foreground" : "text-foreground",
            )}
          >
            {order.customer_name}
          </p>
          <span className="text-[9px] text-muted-foreground">{order.order_number}</span>
        </div>
        {/* Status Badge */}
        <span className={cn("text-[8px] px-1.5 py-0.5 rounded font-medium", statusColor)}>
          {isDelivered && <Check className="inline h-2 w-2 mr-0.5" />}
          {statusLabel}
        </span>
      </div>

      {/* Address Row */}
      <div className="flex items-center gap-1 mb-1.5">
        <MapPin className="h-2.5 w-2.5 text-muted-foreground flex-shrink-0" />
        <span className={cn("text-[10px] truncate", isDelivered ? "text-muted-foreground" : "text-foreground")}>
          {order.address}
        </span>
      </div>

      {/* Phone + Price Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          <Phone className="h-2.5 w-2.5 text-muted-foreground" />
          <span className="text-[9px] text-muted-foreground">{order.customer_phone}</span>
        </div>
        {order.showPrice && order.total_price && (
          <span className={cn("text-[11px] font-bold", isDelivered ? "text-muted-foreground" : "text-primary")}>
            {order.total_price}₾
          </span>
        )}
      </div>

      {/* Action Buttons based on status */}
      {isReady && (
        <button className="w-full h-7 bg-primary text-primary-foreground text-[10px] font-medium rounded flex items-center justify-center gap-1">
          <ArrowRight className="h-3 w-3" />
          {t("landing.demo.startDelivery")}
        </button>
      )}

      {isOutForDelivery && (
        <div className="flex gap-1.5">
          <button className="flex-1 h-7 bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-medium rounded flex items-center justify-center gap-1">
            <Check className="h-3 w-3" />
            {t("landing.demo.delivered")}
          </button>
          <button 
            className="h-7 px-2 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 text-[10px] font-medium rounded flex items-center justify-center"
            aria-label={t("landing.demo.failedDelivery") || "Failed delivery"}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {isDelivered && (
        <div className="flex items-center justify-center gap-1 text-[9px] text-emerald-600 dark:text-emerald-400">
          <Check className="h-3 w-3" />
          <span>{t("landing.demo.delivered")} 14:25</span>
        </div>
      )}
    </div>
  );
}

function PhoneFrame() {
  const { t } = useLanguage();
  const courierOrders = useMemo(() => getDemoCourierOrders(t), [t]);

  return (
    <div className="relative">
      {/* Phone Frame - matching DemoOrderIntake style */}
      <div className="w-[260px] h-[480px] bg-card rounded-[2.5rem] border-4 border-foreground/10 shadow-xl overflow-hidden">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-foreground/10 rounded-full z-10" />

        {/* Screen Content */}
        <div className="h-full pt-8 pb-4 overflow-hidden flex flex-col">
          {/* Header bar */}
          <div className="bg-gradient-to-r from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-white">{t("landing.demo.deliveryList")}</span>
              <span className="text-[10px] text-white/70 bg-white/20 px-1.5 py-0.5 rounded">3 {t("landing.demo.orders").toLowerCase()}</span>
            </div>
          </div>

          {/* Courier cards - scrollable area */}
          <div className="flex-1 px-3 py-3 space-y-2.5 overflow-y-auto bg-muted/30">
            {courierOrders.map((order, i) => (
              <DemoCourierCard key={i} order={order} t={t} />
            ))}
          </div>

          {/* Bottom sync indicator */}
          <div className="px-4 py-2 bg-muted/50 border-t border-border/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-heartbeat" />
                <span className="text-[9px] text-muted-foreground">{t("landing.demo.liveSync")}</span>
              </div>
              <span className="text-[8px] text-muted-foreground">{t("landing.demo.updated")}: 14:30</span>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative glow - matching DemoOrderIntake */}
      <div className="absolute -inset-4 bg-gradient-to-br from-primary/5 to-transparent rounded-[3rem] -z-10" />
    </div>
  );
}

export function DemoCourierFlow() {
  return (
    <div className="relative">
      {/* Desktop: Side by side with arrow */}
      <div className="hidden md:grid md:grid-cols-2 gap-8 items-center">
        <div className="flex justify-end">
          <ModalSnapshot />
        </div>

        {/* Connector Arrow */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="bg-background border border-border rounded-full p-2 shadow-sm">
            <ArrowRight className="h-4 w-4 text-primary" />
          </div>
        </div>

        <div className="flex justify-start">
          <PhoneFrame />
        </div>
      </div>

      {/* Mobile: Stacked layout */}
      <div className="md:hidden flex flex-col items-center gap-6">
        <ModalSnapshot />

        {/* Down arrow for mobile */}
        <div className="flex items-center justify-center">
          <div className="bg-background border border-border rounded-full p-1.5 shadow-sm">
            <ArrowRight className="h-4 w-4 text-primary rotate-90" />
          </div>
        </div>

        <PhoneFrame />
      </div>
    </div>
  );
}